import React from "react";
import {
  GiftIcon,
  UserPlusIcon,
  ChartBarIcon,
  CurrencyDollarIcon,
} from "@heroicons/react/24/outline";
import {
  DocLayout,
  DocBreadcrumb,
  DocTitle,
  DocCard,
  DocAlert,
  DocStepIndicator,
  DocCodeBlock,
  DocNavFooter,
  DocLink,
  DocSection,
} from "@/components/docs";
import { docsColors, docsComponents } from "@/lib/docs-theme";

export default function ReferralProgramPage() {
  const breadcrumbs = [{ label: "Documentation", href: "/docs" }, { label: "Referral Program" }];

  return (
    <DocLayout
      title="Referral Program - Uptime Monitor Documentation"
      description="Turn your professional network into a revenue stream. Earn recurring commissions by referring businesses to Uptime Monitor."
      canonical="https://uptime-monitor.io/docs/referral"
    >
      <DocBreadcrumb items={breadcrumbs} />

      <DocTitle
        title="Referral Program"
        description="Share Uptime Monitor and earn commission rewards."
      />

      {/* Introduction */}
      <DocSection>
        <p className={docsComponents.sectionDescription}>
          Turn your professional network into a revenue stream by referring businesses that need
          reliable uptime monitoring. Earn commission on every successful referral that becomes a
          paying customer.
        </p>
      </DocSection>

      {/* How It Works */}
      <DocCard className="mb-8">
        <div className="flex items-start">
          <GiftIcon className="h-8 w-8 text-blue-500 flex-shrink-0" />
          <div className="ml-4">
            <h2 className={docsComponents.sectionHeading}>How It Works</h2>
            <p className={docsComponents.sectionDescription}>
              Every Uptime Monitor account comes with a unique referral code and link that you can
              share with businesses in your network.
            </p>
            <ul className={`list-disc list-inside ${docsColors.text.body} space-y-2 mt-4`}>
              <li>Get your unique referral link from your dashboard</li>
              <li>Share it with businesses and professionals in your network</li>
              <li>
                When someone signs up using your link, they&apos;re automatically attributed to you
              </li>
              <li>Earn commission when your referrals become paying customers</li>
            </ul>
          </div>
        </div>
      </DocCard>

      {/* Getting Your Referral Link */}
      <div className="space-y-8 mb-8">
        <DocCard>
          <div className="flex items-start">
            <DocStepIndicator number={1} size="large" />
            <div className="ml-4 flex-1">
              <h2 className={docsComponents.sectionHeading}>Access Your Referral Dashboard</h2>
              <p className={docsComponents.sectionDescription}>
                Navigate to the Referral section in your Uptime Monitor dashboard to find your
                unique referral code and link.
              </p>
              <DocLink href="/dashboard/referral" className="inline-flex items-center mt-3">
                Go to Referral Dashboard
                <ChartBarIcon className="h-4 w-4 ml-2" />
              </DocLink>
            </div>
          </div>
        </DocCard>

        <DocCard>
          <div className="flex items-start">
            <DocStepIndicator number={2} size="large" />
            <div className="ml-4 flex-1">
              <h2 className={docsComponents.sectionHeading}>Share Your Link</h2>
              <p className={docsComponents.sectionDescription}>
                Copy your referral link and share it with businesses that need uptime monitoring for
                their critical services.
              </p>
              <DocCodeBlock className="mt-4">
                <p className="text-sm">Your referral link will look like:</p>
                <code className="text-blue-400">
                  https://uptime-monitor.io/accounts/login?ref=YOUR_CODE
                </code>
              </DocCodeBlock>
            </div>
          </div>
        </DocCard>

        <DocCard>
          <div className="flex items-start">
            <DocStepIndicator number={3} size="large" />
            <div className="ml-4 flex-1">
              <h2 className={docsComponents.sectionHeading}>Track Your Earnings</h2>
              <p className={docsComponents.sectionDescription}>
                Monitor your referrals and future commission earnings directly from your dashboard.
                Watch your revenue grow as you help businesses stay online.
              </p>
            </div>
          </div>
        </DocCard>
      </div>

      {/* Where to Share */}
      <DocCard className="mb-8">
        <div className="flex items-start">
          <UserPlusIcon className="h-8 w-8 text-blue-500 flex-shrink-0" />
          <div className="ml-4">
            <h2 className={docsComponents.sectionHeading}>Perfect for Your Professional Network</h2>
            <p className={docsComponents.sectionDescription}>
              If you work with businesses that have online services, you&apos;re perfectly
              positioned to earn from our referral program:
            </p>
            <div className="grid md:grid-cols-2 gap-4 mt-4">
              <div>
                <h3 className="font-semibold text-sm mb-2">Consultants & Agencies</h3>
                <ul className={`text-sm ${docsColors.text.body} space-y-1`}>
                  <li>• Web development agencies</li>
                  <li>• IT consulting firms</li>
                  <li>• Digital marketing agencies</li>
                  <li>• SEO/SEM specialists</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-sm mb-2">Technical Professionals</h3>
                <ul className={`text-sm ${docsColors.text.body} space-y-1`}>
                  <li>• DevOps engineers</li>
                  <li>• System administrators</li>
                  <li>• Site reliability engineers</li>
                  <li>• Technical project managers</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </DocCard>

      {/* Commission System Live */}
      <DocAlert variant="success" className="mb-8">
        <div className="flex items-start">
          <CurrencyDollarIcon className="h-6 w-6 flex-shrink-0 mt-0.5" />
          <div className="ml-3">
            <h3 className="font-semibold">Commission System Now Live!</h3>
            <p className="mt-1">
              Our referral program is now fully operational. Earn 20% lifetime recurring commission
              on every subscription payment from your referrals. The system includes built-in
              protection with a 30-day hold period.
            </p>
          </div>
        </div>
      </DocAlert>

      {/* Commission Protection System */}
      <DocCard className="mb-8">
        <h2 className={docsComponents.sectionHeading}>Commission Protection System</h2>
        <div className="space-y-4">
          <div>
            <h3 className="font-semibold text-sm mb-2">30-Day Hold Period</h3>
            <p className={`text-sm ${docsColors.text.body}`}>
              All new commissions are held for 30 days before becoming available for payout. This
              protects against refunds, chargebacks, and subscription cancellations, ensuring you
              only earn from genuine, committed customers.
            </p>
          </div>
          <div>
            <h3 className="font-semibold text-sm mb-2">Earnings Tracking</h3>
            <p className={`text-sm ${docsColors.text.body}`}>
              Your dashboard shows two types of earnings: <strong>Available Earnings</strong> (ready
              for payout) and <strong>Pending Earnings</strong> (in the 30-day hold period). This
              transparency lets you track exactly when commissions become payable.
            </p>
          </div>
        </div>
      </DocCard>

      {/* Technical Details */}
      <DocCard className="mb-8">
        <h2 className={docsComponents.sectionHeading}>Technical Details</h2>
        <div className="space-y-4">
          <div>
            <h3 className="font-semibold text-sm mb-2">How Attribution Works</h3>
            <p className={`text-sm ${docsColors.text.body}`}>
              When someone clicks your referral link, your referral code is stored in their browser.
              If they sign up within 30 days, you&apos;ll be credited as the referrer. The
              attribution is permanent and tied to their account.
            </p>
          </div>
          <div>
            <h3 className="font-semibold text-sm mb-2">Referral Code Format</h3>
            <p className={`text-sm ${docsColors.text.body}`}>
              Each referral code is a unique 8-character alphanumeric string (e.g., ABCD1234)
              that&apos;s automatically generated for your account. You can find both your code and
              full referral link in your dashboard.
            </p>
          </div>
          <div>
            <h3 className="font-semibold text-sm mb-2">Privacy & Data</h3>
            <p className={`text-sm ${docsColors.text.body}`}>
              Referral tracking respects user privacy. We only count successful signups and
              don&apos;t store any personal information about users who click your link but
              don&apos;t sign up.
            </p>
          </div>
        </div>
      </DocCard>

      {/* Navigation */}
      <DocNavFooter
        prev={{ text: "Best Practices", href: "/docs/best-practices" }}
        next={{ text: "API Overview", href: "/docs/api" }}
      />
    </DocLayout>
  );
}
